import React, { useState, useEffect } from 'react';
import { Deck, _GlobeView as GlobeView } from '@deck.gl/core';
import { SolidPolygonLayer, GeoJsonLayer, IconLayer, ArcLayer } from '@deck.gl/layers';
import { DeckGL } from 'deck.gl';
import { Link } from 'react-router-dom';
import Legend from './Legend';
import './map.css';

import { ethers } from 'ethers';
import {
    catastropheBondsFactoryAddress_Fuji,
    catastropheBondsFactoryAddress_Amoy,
} from "../config/config.js";

import catastropheBondsFactoryAbi from "../config/abis/catastropheBondsFactory.json";
import catastropheBondAbi from "../config/abis/catastropheBond.json";

// source: Natural Earth http://www.naturalearthdata.com/ via geojson.xyz
const COUNTRIES = 'https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_50m_admin_0_scale_rank.geojson'; //eslint-disable-line
// const rootUrl = "https://europe-west3-shamba-oracle-services.cloudfunctions.net/shamba-geo-insurance-api";
const rootUrl = "https://europe-west3-shamba-staging-environment.cloudfunctions.net/cat-bonds-api-test"

const settledToStatus = {
    "0": "NONE",
    "1": "ORACLE",
    "2": "FLOOR",
    "3": "CAP"
}

const INITIAL_VIEW_STATE = {
    latitude: 10.281104,
    longitude: 32.751666,
    zoom: 1
};

export default function Map({ data, filteredData, selectedAddress }) {

    const [isLoading, setIsLoading] = useState(false);
    const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
    const galaxyColors = [
        '#220033',
        '#330055',
        '#440077',
        '#550099',
        '#6600BB',
        '#7700DD',
        '#8800FF',
        '#7700DD',
        '#6600BB',
        '#550099',
        '#440077',
        '#330055',
        '#220033',
    ];

    const legendData = [
        { label: 'Countries', color: [147, 77, 158], layer: 'base-map' },
        { label: 'Project Areas', color: [255, 159, 0, 180], layer: 'projects-layer' },
        { label: 'Project Points', color: [0, 255, 0, 180], layer: 'projects-marker-layer' }
    ];
    const [projectGeoData, setProjectGeoData] = useState(null);
    const [popupInfo, setPopupInfo] = useState(null);
    // Compute centroid of a simple polygon
    const computeCentroid = (coordinates) => {
        let sumX = 0;
        let sumY = 0;
        const len = coordinates[0].length; // Assuming your polygons are simple and without holes

        coordinates[0].forEach(coord => {
            sumX += coord[0];
            sumY += coord[1];
        });

        return [sumX / len, sumY / len];
    };

    function formatDate(timestamp) {
        // Step 1: Create a Date object from the timestamp by converting from seconds to milliseconds
        const date = new Date(timestamp * 1000);

        // Step 2: Extract the year, month, and day
        const year = date.getFullYear();
        let month = date.getMonth() + 1; // getMonth() returns month from 0 to 11
        let day = date.getDate();

        // Step 3: Format month and day to ensure two digits
        if (month < 10) {
            month = '0' + month;
        }
        if (day < 10) {
            day = '0' + day;
        }

        // Step 4: Construct the final date string
        return `${year}-${month}-${day}`;
    }

    function formatString(str) {
        return str
            .split('_')                   // Split the string by underscores
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()  // Capitalize each word
            )
            .join(' ');                   // Join the words with spaces
    }

    function getCatastropheEmoji(catastropheCode) {
        if (catastropheCode == 'wild_fire')
            return '🔥';
        else if (catastropheCode == 'heatwave')
            return '☀️';
        else if (catastropheCode == 'floods')
            return '🌊';
        else if (catastropheCode == 'drought')
            return '🏜️';
    }

    async function getLocationGeoJSON(location) {
        return fetch(`${rootUrl}/getLocationGeoJSON?location=${location}`)
            .then(response => response.json())
            .then(data => {
                return data;
            })
    }

    async function getAllCatastropheBonds() {
        try {
            const provider = new ethers.BrowserProvider(window.ethereum);

            const chainId = parseInt((await provider.getNetwork()).chainId.toString());

            let catastropheBondsFactoryContract;

            if (chainId == 43113) {
                catastropheBondsFactoryContract = new ethers.Contract(catastropheBondsFactoryAddress_Fuji, catastropheBondsFactoryAbi, provider);

            }
            else if (chainId == 80002) {
                catastropheBondsFactoryContract = new ethers.Contract(catastropheBondsFactoryAddress_Amoy, catastropheBondsFactoryAbi, provider);
            }
            else {
                alert("Network not supported")
                return;
            }

            const catastropheBonds = await catastropheBondsFactoryContract.getAllCatastropheBonds();

            return catastropheBonds;
        }
        catch (error) {
            console.error(error.toString())
            console.log("Unknown error.");

            return [];
        }
    }

    async function getCatastropheBondDetails(catastropheAddress) {
        try {
            const provider = new ethers.BrowserProvider(window.ethereum);

            const catastropheBondContract = new ethers.Contract(catastropheAddress, catastropheBondAbi, provider);

            const catastropheCode = await catastropheBondContract.getCatastropheCode();
            const location = await catastropheBondContract.getLocation();
            const startDate_Timestamp = await catastropheBondContract.getStartDate_Timestamp();
            const endDate_Timestamp = await catastropheBondContract.getEndDate_Timestamp();
            const collateralRequiredForMintingAnyPositionToken = await catastropheBondContract.getCollateralRequiredForMintingAnyPositionToken();
            const settledTo = await catastropheBondContract.settledTo();
            const isSettled = await catastropheBondContract.isSettled();

            const collateralRequiredForMintingAnyPositionToken_formatted = parseFloat(ethers.formatUnits(collateralRequiredForMintingAnyPositionToken, "mwei"));

            const catastropheEmoji = getCatastropheEmoji(catastropheCode);



            const title = `${catastropheEmoji} ${formatString(catastropheCode)} at ${location}`;

            const chainId = parseInt((await provider.getNetwork()).chainId.toString());

            let blockchainExplorerUrl;

            if (chainId == 43113) {
                blockchainExplorerUrl = `https://testnet.snowtrace.io/address/${catastropheAddress}`;

            }
            else if (chainId == 80002) {
                blockchainExplorerUrl = `https://amoy.polygonscan.com/address/${catastropheAddress}`;
            }

            const catastropheBondDetails = {
                title: title,
                catastropheEmoji: getCatastropheEmoji(catastropheCode),
                catastrophe: formatString(catastropheCode),
                catastropheAddress: catastropheAddress,
                blockchainExplorerUrl: blockchainExplorerUrl,
                location: location,
                startDate: formatDate(parseInt(startDate_Timestamp)),
                endDate: formatDate(parseInt(endDate_Timestamp)),
                collateralRequiredForMintingAnyPositionToken: collateralRequiredForMintingAnyPositionToken_formatted,
                isSettled: isSettled,
                settledTo: settledToStatus[settledTo],
                geoJSON: (await getLocationGeoJSON(location)).data,
            }

            // console.dir(catastropheBondDetails, { depth: null });
            return catastropheBondDetails;

        }
        catch (error) {
            console.error(error.toString())
            console.log("Unknown error.");
            return {};
        }
    }

    useEffect(() => {

        // fetch(`${rootUrl}/getInsuranceContracts`)
        //     .then(response => response.json())
        //     .then(data => {
        //         const transformedData = {
        //             type: 'FeatureCollection',
        //             features: data.data.map(project => {
        //                 const geometry = project.requestBody.geometry.features[0].geometry;
        //                 const centroid = geometry.type === 'Polygon' ? computeCentroid(geometry.coordinates) : null;
        //                 return {
        //                     type: 'Feature',
        //                     geometry: geometry,
        //                     properties: {
        //                         ...project.requestBody.geometry.features[0].properties,
        //                         insuranceContractAddress: project.insuranceContractAddress,
        //                         contractName: project.location,
        //                         insurerAddress: project.insurerAddress,
        //                         requestIpfsCid: project.requestIpfsCid,
        //                         ethChainId: project.ethChainId,
        //                         centroid: centroid
        //                     }
        //                 }
        //             })
        //         };
        //         setProjectGeoData(transformedData);
        //         // console.log(transformedData)
        //     })
        //     .catch((error) => {
        //         console.error('Error fetching data:', error);
        //     });


        getAllCatastropheBonds().then(async (catastropheBonds) => {
            setIsLoading(true);
            let catastropheBondsDetails = [];
            for (var i = 0; i < catastropheBonds.length; i++) {
                catastropheBondsDetails.push(await getCatastropheBondDetails(catastropheBonds[i]));
            }
            const transformedData = {
                type: 'FeatureCollection',
                features: catastropheBondsDetails.map(catastropheBond => {
                    const geometry = catastropheBond.geoJSON.features[0].geometry;
                    const centroid = geometry.type === 'Polygon' ? computeCentroid(geometry.coordinates) : null;
                    return {
                        type: 'Feature',
                        geometry: geometry,
                        properties: {
                            ...catastropheBond.geoJSON.features[0].properties,
                            catastropheAddress: catastropheBond.catastropheAddress,
                            blockchainExplorerUrl: catastropheBond.blockchainExplorerUrl,
                            title: catastropheBond.title,
                            catastropheEmoji: catastropheBond.catastropheEmoji,
                            location: catastropheBond.location,
                            duration: `${catastropheBond.startDate} to ${catastropheBond.endDate}`,
                            collateralRequiredForMintingAnyPositionToken: catastropheBond.collateralRequiredForMintingAnyPositionToken,
                            isSettled: catastropheBond.isSettled,
                            settledTo: catastropheBond.settledTo,
                            centroid: centroid
                        }
                    }
                })
            };
            setProjectGeoData(transformedData);
            setIsLoading(false);
        }).catch((error) => {
            console.error('Error fetching data:', error);
            alert('Unable to fetch data')
            setIsLoading(false);
        });


    }, []);
    useEffect(() => {
        if (selectedAddress && data) {
            const selectedFeature = data.find(
                feature => feature.properties.insuranceContractAddress === selectedAddress
            );

            if (selectedFeature && selectedFeature.geometry.type === 'Polygon') {
                const [longitude, latitude] = computeCentroid(selectedFeature.geometry.coordinates);
                setViewState(prev => ({
                    ...prev,
                    latitude,
                    longitude,
                    zoom: 6 // Set to a desired zoom level for a feature
                }));
            } // Additional geometry types can be added if necessary

        }
    }, [selectedAddress, data]);

    function getTooltip({ object }) {

        if (object && object.properties) {

            return (
                object && {
                    html: `\
                <div><b>Name</b></div>
                <div><a href="${object.properties.blockchainExplorerUrl}">${object.properties.title}</a></div> 
                <div><b>Contract</b></div>
                <div>${object.properties.catastropheAddress}</div>                
                `
                }
            );
        }

        return null
    }
    const renderPopup = () => {
        return popupInfo && (
            <div
                style={{
                    position: 'absolute',
                    zIndex: 1,
                    left: popupInfo.x,
                    top: popupInfo.y,
                    pointerEvents: 'auto', // Ensure pointer events are enabled
                }}
            >
                <div style={{ background: '#22242F', padding: '10px', borderRadius: '5px', boxShadow: '0px 0px 10px rgba(0,0,0,0.5)' }}>
                    <div>
                        <div><b>Name</b></div>
                        <div>
                            <a href={`${popupInfo.object.properties.blockchainExplorerUrl}`}>
                                {popupInfo.object.properties.title}
                            </a>
                        </div>
                        <div><b>Contract</b></div>
                        <div>{popupInfo.object.properties.catastropheAddress}</div>
                        <div><b>Duration</b></div>
                        <div>{popupInfo.object.properties.duration}</div>
                        <div><b>Settlement Status</b></div>
                        {popupInfo.object.properties.isSettled ? <div style={{ color: 'green' }}>SETTLED</div> : <div style={{ color: 'yellow' }}>NOT SETTLED</div>}
                        {/* <div><b>IPFS Reference</b></div>
                        <a href={`https://${popupInfo.object.properties.requestIpfsCid}.ipfs.dweb.link`} target='blank'>{popupInfo.object.properties.requestIpfsCid}</a> */}
                    </div>
                    <br />
                    <button onClick={(e) => { e.stopPropagation(); setPopupInfo(null); }}>Close</button>
                    {/* <a href={`/contract/${popupInfo.object.properties.catastropheAddress}`} target="_blank">
                        <button>View More</button>
                    </a> */}
                </div>
            </div>
        );
    }

    const controlStyles = {
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 2,
        fontSize: '28px',
        display: 'flex',
        flexDirection: 'column', // stack buttons vertically
        gap: '5px'  // space between buttons
    };
    function zoomIn() {
        setViewState(prevState => ({
            ...prevState,
            zoom: prevState.zoom + 1
        }));
    }

    function zoomOut() {
        setViewState(prevState => ({
            ...prevState,
            zoom: prevState.zoom - 1
        }));
    }

    // Define the icon URL and size
    const iconURL = 'https://bafybeiaxkc2f2ouluwaedfrz56bhsrauqgztfqegt3jhyltcdqnnxpu6ui.ipfs.w3s.link/icons8-location-pin-64.png'
    //'https://bafybeievujalxwz2pqxf2tcsm27p62gkiwmsjw327nxlfa5mxxecjlxoui.ipfs.w3s.link/icon.png'
    //'https://img.icons8.com/dusk/64/000000/user-location.png'

    const iconSize = [64, 64]; // Specify the size of the icon

    return (
        <div>
            {isLoading ? <div>Loading...</div> : <div></div>}
            <div style={{ background: '#09101D' }} class="vector-map">
                <div style={controlStyles}>
                    <button onClick={zoomIn}>+</button>
                    <button onClick={zoomOut}>-</button>

                </div>
                {renderPopup()}
                <div id="map" style={{ left: '12px', top: '0px', width: '100%', position: 'absolute', height: '100%' }}>

                    <DeckGL
                        onClick={(info, event) => {
                            // If you click on anything other than a feature, hide the popup.
                            if (!info.object) {
                                setPopupInfo(null);
                            }
                            event.stopPropagation();
                        }}
                        views={new GlobeView()}
                        viewState={viewState}
                        onViewStateChange={({ viewState }) => setViewState(viewState)}

                        controller={true}
                        getTooltip={getTooltip}

                        layers={[
                            new SolidPolygonLayer({
                                id: 'background',
                                data: [
                                    // prettier-ignore
                                    [[-180, 90], [0, 90], [180, 90], [180, -90], [0, -90], [-180, -90]]
                                ],
                                opacity: 0.9,
                                getPolygon: d => d,
                                stroked: true,
                                filled: true,
                                getFillColor: galaxyColors,
                            }),
                            new GeoJsonLayer({
                                id: 'base-map',
                                data: COUNTRIES,
                                // Styles
                                stroked: true,
                                filled: true,
                                lineWidthMinPixels: 2,
                                getLineColor: [116, 50, 130],
                                getFillColor: [31, 38, 42]
                            }),

                            projectGeoData && projectGeoData.features ? projectGeoData.features.map((feature, index) => {
                                // Create a canvas element
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                const size = 64; // Size of the icon

                                // Set the canvas size
                                canvas.width = size;
                                canvas.height = size;

                                // Draw the emoji onto the canvas
                                ctx.font = `${size}px serif`;
                                ctx.textAlign = 'center';
                                ctx.textBaseline = 'middle';
                                ctx.fillText(feature.properties.catastropheEmoji, size / 2, size / 2);

                                // Convert canvas to data URL
                                const iconAtlas = canvas.toDataURL();

                                return new IconLayer({
                                    id: `icon-layer-${index}`,
                                    data: [
                                        {
                                            type: 'Feature',
                                            position: feature.properties.centroid,
                                            properties: feature.properties
                                        }
                                    ],
                                    pickable: true,
                                    iconAtlas: iconAtlas,
                                    iconMapping: {
                                        marker: {
                                            x: 0,
                                            y: 0,
                                            width: iconSize[0],
                                            height: iconSize[1],
                                        },
                                    },
                                    getPosition: (d) => d.position,
                                    getIcon: (d) => "marker",
                                    getSize: 5, // Adjust as needed
                                    sizeScale: 5, // Adjust as needed
                                    onClick: info => {
                                        // console.log(info)
                                        setPopupInfo(info);
                                    },
                                    visible: viewState.zoom <= 5
                                });
                            }) : [],


                            new GeoJsonLayer({
                                id: 'projects-layer',
                                data: projectGeoData,
                                pickable: true,
                                stroked: true,
                                filled: false,
                                pointType: 'circle',
                                lineWidthScale: 40,
                                lineWidthMinPixels: 4,
                                getLineColor: [255, 159, 0, 100],
                                getFillColor: [0, 35, 100, 0],
                                getPointRadius: 300,
                                getLineWidth: 10,
                                visible: viewState.zoom > 5,
                                onClick: (info, event) => {
                                    setPopupInfo(info);
                                    event.stopPropagation();
                                }
                            })

                        ]}
                    />
                </div>
                {/* <Legend data={legendData} position="bottom-right" title="Legend" /> */}
            </div>
        </div>
    )
}